// IndexedDB store for user-dismissed alert keys.
//
// Modelled on pinned-store.ts, but kept as a sibling rather than a new
// object-store inside the pinned DB. Reason: bumping the pinned DB's
// version blocks open tabs (the pinned-store comment explicitly warns
// against this), and the two concerns have different lifetimes — pinned
// entries are user-curated and forever, dismissed entries auto-expire
// after 7 days.
//
// Database layout:
//   DB:     "eidollon-dismissed-alerts" (version 1, never bumped)
//   Store:  "dismissed" — key = composite "{tenantId}|{alertKey}",
//                          value = { tenant, key, dismissed_at }
//   Index:  "by_tenant" on [tenant] for efficient per-tenant list + sweep.
//
// `alertKey` is the 16-hex output of computeAlertKey() from the terminal's
// alert-dedup helper. Tenant isolation: every op is keyed by composite
// (tenant, key); list/sweep operations open a cursor on the tenant index
// so the engine physically cannot return another tenant's rows.

export const DISMISSED_DB_NAME = 'eidollon-dismissed-alerts';
export const DISMISSED_DB_VERSION = 1;
export const DISMISSED_STORE = 'dismissed';
export const DISMISSED_INDEX_BY_TENANT = 'by_tenant';

/** Dismissed entries expire after 7 days. Surfaced as a constant so tests
 *  and the hydration path agree on the same TTL. */
export const DISMISSED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface DismissedEntry {
  /** 16-hex alert key from computeAlertKey(). */
  key: string;
  /** ms-epoch when the user dismissed this alert. */
  dismissed_at: number;
}

interface StoredDismissedRow {
  /** Composite primary key — `${tenant}|${key}`. */
  rowKey: string;
  tenant: string;
  key: string;
  dismissed_at: number;
}

export interface DismissedStore {
  add(key: string): Promise<DismissedEntry>;
  remove(key: string): Promise<void>;
  has(key: string): Promise<boolean>;
  /** All non-expired entries for this tenant. Expired rows are deleted in
   *  the same readwrite transaction (passive cleanup on every list call). */
  list(now?: number): Promise<DismissedEntry[]>;
  clear(): Promise<void>;
  close(): void;
}

export interface OpenDismissedStoreOptions {
  tenantId: string;
  /** Override for tests (fake-indexeddb). */
  factory?: IDBFactory;
}

function rowKeyOf(tenant: string, key: string): string {
  // `|` is illegal in our 16-hex keys and in sanitised tenant ids, so this
  // composite is collision-free.
  return `${tenant}|${key}`;
}

export async function openDismissedStore(opts: OpenDismissedStoreOptions): Promise<DismissedStore> {
  const factory = opts.factory ?? (typeof indexedDB !== 'undefined' ? indexedDB : null);
  if (!factory) throw new Error('IndexedDB not available in this environment');

  const tenant = opts.tenantId;

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = factory.open(DISMISSED_DB_NAME, DISMISSED_DB_VERSION);
    req.onupgradeneeded = () => {
      const upDb = req.result;
      if (!upDb.objectStoreNames.contains(DISMISSED_STORE)) {
        const os = upDb.createObjectStore(DISMISSED_STORE, { keyPath: 'rowKey' });
        os.createIndex(DISMISSED_INDEX_BY_TENANT, 'tenant', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });

  async function add(key: string): Promise<DismissedEntry> {
    const row: StoredDismissedRow = {
      rowKey: rowKeyOf(tenant, key),
      tenant,
      key,
      dismissed_at: Date.now(),
    };
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(DISMISSED_STORE, 'readwrite');
      t.objectStore(DISMISSED_STORE).put(row);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
    return { key: row.key, dismissed_at: row.dismissed_at };
  }

  async function remove(key: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(DISMISSED_STORE, 'readwrite');
      t.objectStore(DISMISSED_STORE).delete(rowKeyOf(tenant, key));
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  }

  async function has(key: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const req = db
        .transaction(DISMISSED_STORE, 'readonly')
        .objectStore(DISMISSED_STORE)
        .getKey(rowKeyOf(tenant, key));
      req.onsuccess = () => resolve(req.result !== undefined);
      req.onerror = () => reject(req.error);
    });
  }

  async function list(now: number = Date.now()): Promise<DismissedEntry[]> {
    return new Promise((resolve, reject) => {
      const out: DismissedEntry[] = [];
      const t = db.transaction(DISMISSED_STORE, 'readwrite');
      const ix = t.objectStore(DISMISSED_STORE).index(DISMISSED_INDEX_BY_TENANT);
      const range = IDBKeyRange.only(tenant);
      const req = ix.openCursor(range);
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return; // resolved by tx.oncomplete
        const v = cur.value as StoredDismissedRow;
        if (now - v.dismissed_at > DISMISSED_TTL_MS) {
          cur.delete();
        } else {
          out.push({ key: v.key, dismissed_at: v.dismissed_at });
        }
        cur.continue();
      };
      req.onerror = () => reject(req.error);
      t.oncomplete = () => {
        out.sort((a, b) => b.dismissed_at - a.dismissed_at);
        resolve(out);
      };
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  }

  async function clear(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(DISMISSED_STORE, 'readwrite');
      const ix = t.objectStore(DISMISSED_STORE).index(DISMISSED_INDEX_BY_TENANT);
      const range = IDBKeyRange.only(tenant);
      const req = ix.openCursor(range);
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        cur.delete();
        cur.continue();
      };
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  }

  return {
    add,
    remove,
    has,
    list,
    clear,
    close: () => db.close(),
  };
}
